/*
 * A search hit that is a message, drawn by the module that owns messages (UX §8 #10, I46).
 *
 * Core draws the row (`core/search/HitRow.tsx`); what this file adds is the three things core cannot
 * know — that the hit is a message, so its author is a person with a face and a name; which
 * conversation it is in as that conversation is *named* (the one naming ladder of
 * `core/store/conversationName.ts`: the handle for a direct conversation, the address for every
 * other); and, where the match was the name of a file, which files are on the message. A list of
 * excerpts with no author and no place is the thing testers reported: "which of these is Bea's, and
 * where?"
 *
 * **A place is named wherever it can be named** (X-B4 B4-9). The model holds the conversations the
 * reader is in; a public channel they have never joined is not in it, and the row used to call it "a
 * conversation you have left" while printing its whole content. The public directory
 * (`conversation/publicDirectory.ts`, the same browse answer the quick switcher reads) names it, and
 * the *Not joined* pill the row already draws says the rest: `#support · Not joined`. The neutral
 * sentence is left for the one case it was ever true of — a conversation no directory may name, which
 * is a private one the reader is not in.
 *
 * The address is handed in. `SearchLocator` (routes/links.ts) is the one place that knows this
 * build's URL shapes, and a module that built its own would answer `/c/…` for a direct message.
 */

import { useEffect, useMemo, useState } from "react"
import { t as coreT } from "../../../core/i18n/index.ts"
import { PersonFace } from "../../../core/people/PersonChip.tsx"
import { HitRow } from "../../../core/search/HitRow.tsx"
import { FLAG_FILE, type SearchResult } from "../../../core/search/api.ts"
import { conversationAddress, conversationHandle } from "../../../core/store/conversationName.ts"
import { useModel } from "../../../core/store/store.ts"
import { publicChannel, refreshPublicDirectory } from "../conversation/publicDirectory.ts"
import { t } from "../i18n/t.ts"

/**
 * `searchKindMessage` (internal/modules/chat/search_source.go): the number chat's search source
 * registers with core, which is why the slot is keyed by a number here rather than by a string a
 * module could spell two ways.
 */
export const KIND_MESSAGE = 0

/**
 * How long a row waits before looking at the directory again, and how many times. The wait doubles:
 * the answer is one local request away, so the first look is soon enough that nobody sees the sentence
 * it replaces, and the last one is late enough to cover a slow answer without looking for ever
 * (60 + 120 + 240 + 480 + 960 ms ≈ two seconds in all).
 */
const DIRECTORY_RETRY_MS = 60
const DIRECTORY_TRIES = 5

export function SearchHit({
	result,
	href,
	onNavigate,
}: {
	result: SearchResult
	href: string | null
	onNavigate?: ((path: string) => void) | undefined
}) {
	const author = useModel((model) => model.users[result.authorUserId])
	const name = author?.displayName ?? t("chat.search.unknownAuthor")
	// The place as the ladder names it (X-A3 V21): a direct conversation by the handle of the person on
	// the other side, so a hit in one does not print "Bea Bergmann" as the author and "Bea Bergmann" as
	// the place. An empty answer means the model does not hold the conversation.
	const known = useModel(
		(model) =>
			conversationHandle(model, result.scopeId) ?? conversationAddress(model, result.scopeId),
	)
	const channel = usePublicChannel(result.scopeId, known === "")
	const slug = channel?.conversation.slug ?? ""
	const place = known !== "" ? known : slug === "" ? coreT("search.group.unknown") : `#${slug}`

	// The files of the message, for a hit whose excerpt holds nothing marked: then the query matched a
	// file name, and the window the reader has loaded is the only place the names are (X-B4 B4-15).
	// The selector answers with the store's own array and the names are mapped outside it — a selector
	// that builds a new array on every read is a render loop.
	const matchedElsewhere =
		(result.flags & FLAG_FILE) !== 0 && !result.snippet.parts.some((part) => part.m === true)
	const attachments = useModel((model) =>
		matchedElsewhere
			? model.messages[result.scopeId]?.rows.find((row) => row.seq === result.scopeSeq)?.attachments
			: undefined,
	)
	const files = useMemo(() => attachments?.map((file) => file.filename), [attachments])

	return (
		<HitRow
			result={result}
			href={href}
			onNavigate={onNavigate}
			place={place}
			placeNamed={place !== coreT("search.group.unknown")}
			files={files}
			avatar={
				<PersonFace person={{ id: result.authorUserId, name, avatarUrl: author?.avatarUrl }} />
			}
			author={name}
		/>
	)
}

/**
 * The public channel behind a conversation the model does not hold, once the directory can answer for
 * it.
 *
 * The directory answers from memory, and the request for a fresh one is made from this hook's effect,
 * so the first row drawn on a cold page has nothing yet. It is module state with nothing to subscribe
 * to — so the row looks again a few times, briefly, and then stops: a name that arrives a third of a
 * second late is worth four looks, and a row that looks for ever is not.
 */
function usePublicChannel(conv: number, needed: boolean) {
	const [tries, setTries] = useState(0)
	const found = needed ? publicChannel(conv) : undefined
	useEffect(() => {
		if (!needed || found !== undefined || tries >= DIRECTORY_TRIES) return
		// The ask is made here and not while the row renders: reading the directory answers out of
		// memory, and a page of hits that each started a request would be a render pass asking the
		// network. The module itself decides whether anything leaves — at most once a minute.
		refreshPublicDirectory()
		const timer = setTimeout(
			() => {
				setTries((before) => before + 1)
			},
			DIRECTORY_RETRY_MS * 2 ** tries,
		)
		return () => {
			clearTimeout(timer)
		}
	}, [needed, found, tries])
	return found
}
